/**
 * Momentum / recovery-bar team colour resolver (EPIC-111 D2, D3; supersedes
 * the EPIC-110 D4 near-neutral green/blue fallback). `resolveChartTeamPair`
 * is unchanged for every other chart.
 */
import { hexToRgb, rgbToHsl, type ChartTeamColorInput } from "../chartColor";

export const MOMENTUM_NEUTRAL_SATURATION_MAX = 0.25;
export const MOMENTUM_HOME_FALLBACK = "#22c55e";
export const MOMENTUM_AWAY_FALLBACK = "#3b82f6";
export const MOMENTUM_AWAY_SECOND_FALLBACK = "#a855f7";
/** Last resort, only reached if green + blue + purple all clash with home. */
const MOMENTUM_LAST_RESORT = "#f97316";

const MIN_HUE_DISTANCE_DEG = 40;
const MIN_LIGHTNESS_GAP = 0.25;

function hsl(hex: string): [number, number, number] | null {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHsl(...rgb) : null;
}

/** True for black / white / grey kits (HSL saturation below 0.25). */
export function isNearNeutral(hex: string): boolean {
  const c = hsl(hex);
  return c ? c[1] < MOMENTUM_NEUTRAL_SATURATION_MAX : false;
}

/** Hue distance >= 40 degrees OR HSL lightness gap >= 0.25. */
export function areMomentumColorsDistinguishable(
  a: string,
  b: string,
): boolean {
  const ca = hsl(a);
  const cb = hsl(b);
  if (!ca || !cb) return true;
  const hueDelta = Math.abs(ca[0] - cb[0]) * 360;
  const hueDistance = Math.min(hueDelta, 360 - hueDelta);
  return (
    hueDistance >= MIN_HUE_DISTANCE_DEG ||
    Math.abs(ca[2] - cb[2]) >= MIN_LIGHTNESS_GAP
  );
}

/** Chart card background (`bg-slate-900`) the contrast rule measures against. */
export const MOMENTUM_CARD_BG = "#0f172a";
/** Outline drawn around a fill that is too dark to read on the card. */
export const MOMENTUM_OUTLINE = "#cbd5e1";
export const MOMENTUM_OUTLINE_MIN_CONTRAST = 3;

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function luminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return (
    0.2126 * channel(rgb[0]) +
    0.7152 * channel(rgb[1]) +
    0.0722 * channel(rgb[2])
  );
}

/** WCAG contrast ratio of a colour against the chart card background. */
export function contrastOnCard(hex: string): number {
  const l = luminance(hex);
  const bg = luminance(MOMENTUM_CARD_BG) ?? 0;
  if (l === null) return 21;
  const hi = Math.max(l, bg);
  const lo = Math.min(l, bg);
  return (hi + 0.05) / (lo + 0.05);
}

/** `#cbd5e1` iff the fill's contrast on the card is below 3:1, else undefined. */
export function momentumStrokeFor(hex: string): string | undefined {
  return contrastOnCard(hex) < MOMENTUM_OUTLINE_MIN_CONTRAST
    ? MOMENTUM_OUTLINE
    : undefined;
}

/**
 * True kit colours (EPIC-111 D2): each team draws in its own primary
 * (secondary only when the primary is missing/unparseable). Visibility on the
 * dark card comes from an outline (`momentumStrokeFor`), never a colour swap.
 * Only an indistinguishable pair moves the away team: its secondary first,
 * then the existing fallback chain.
 */
export function resolveMomentumColors(
  home: ChartTeamColorInput,
  away: ChartTeamColorInput,
): { home: string; away: string } {
  const own = (c: ChartTeamColorInput) =>
    hexToRgb(c.primary ?? "")
      ? c.primary
      : c.secondary && hexToRgb(c.secondary)
        ? c.secondary
        : c.primary;
  const homeColor = own(home);
  let awayColor = own(away);

  if (!areMomentumColorsDistinguishable(homeColor, awayColor)) {
    const candidates = [
      away.secondary,
      MOMENTUM_AWAY_FALLBACK,
      MOMENTUM_AWAY_SECOND_FALLBACK,
      MOMENTUM_LAST_RESORT,
      "#ffffff",
      "#000000",
    ].filter((c): c is string => !!c && !!hexToRgb(c));
    for (const candidate of candidates) {
      if (areMomentumColorsDistinguishable(homeColor, candidate)) {
        awayColor = candidate;
        break;
      }
    }
  }
  return { home: homeColor, away: awayColor };
}
